import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

const LOCALE_MAP = { en: "en-GB", ru: "ru-RU", ar: "ar-EG", zh: "zh-CN" };
const curLocale = () => LOCALE_MAP[i18n.language] || "en-GB";

const ROLE_STYLES = {
  "Super Admin": "bg-amber-100 text-amber-900 border-amber-300",
  Client: "bg-sky-100 text-sky-900 border-sky-300",
  "Client Consultant": "bg-indigo-100 text-indigo-900 border-indigo-300",
  "Client Designer": "bg-purple-100 text-purple-900 border-purple-300",
  "Sub-Contractor": "bg-orange-100 text-orange-900 border-orange-300",
  "Lead Designer of Structure": "bg-teal-100 text-teal-900 border-teal-300",
  "Acrylic Supplier": "bg-emerald-100 text-emerald-900 border-emerald-300",
  Supplier: "bg-emerald-100 text-emerald-900 border-emerald-300",
  "Main Contractor": "bg-blue-100 text-blue-900 border-blue-300",
};

export function RoleBadge({ role, className = "", ...props }) {
  const cls = ROLE_STYLES[role] || "bg-slate-100 text-slate-800 border-slate-300";
  return (
    <Badge variant="outline" className={`font-mono text-xs whitespace-nowrap ${cls} ${className}`} {...props}>
      {role}
    </Badge>
  );
}

const STATUS_STYLES = {
  pending: "bg-amber-100 text-amber-900 border-amber-300",
  fulfilled: "bg-emerald-100 text-emerald-900 border-emerald-300",
  cancelled: "bg-slate-200 text-slate-600 border-slate-300",
  open: "bg-sky-100 text-sky-900 border-sky-300",
  answered: "bg-emerald-100 text-emerald-900 border-emerald-300",
  closed: "bg-slate-200 text-slate-700 border-slate-300",
  sent: "bg-sky-100 text-sky-900 border-sky-300",
  approved: "bg-amber-100 text-amber-900 border-amber-300",
  paid: "bg-emerald-100 text-emerald-900 border-emerald-300",
  overdue: "bg-rose-100 text-rose-900 border-rose-300",
  accepted: "bg-emerald-100 text-emerald-900 border-emerald-300",
  expired: "bg-slate-200 text-slate-700 border-slate-300",
  active: "bg-emerald-100 text-emerald-900 border-emerald-300",
  payment: "bg-sky-100 text-sky-900 border-sky-300",
  ipc: "bg-violet-100 text-violet-900 border-violet-300",
};

export function StatusBadge({ status, label, className = "", ...props }) {
  const { t } = useTranslation();
  const cls = STATUS_STYLES[(status || "").toLowerCase()] || "bg-slate-100 text-slate-800 border-slate-300";
  const key = (status || "").toLowerCase();
  return (
    <Badge variant="outline" className={`font-mono text-xs uppercase tracking-wide whitespace-nowrap ${cls} ${className}`} {...props}>
      {label || t(`badges.${key}`, { defaultValue: status })}
    </Badge>
  );
}

const PRIORITY_STYLES = {
  Low: "bg-slate-100 text-slate-700 border-slate-300",
  Medium: "bg-sky-100 text-sky-900 border-sky-300",
  High: "bg-amber-100 text-amber-900 border-amber-300",
  Urgent: "bg-rose-100 text-rose-900 border-rose-300",
};

export function PriorityBadge({ priority, ...props }) {
  const { t } = useTranslation();
  const cls = PRIORITY_STYLES[priority] || PRIORITY_STYLES.Medium;
  return (
    <Badge variant="outline" className={`font-mono text-xs uppercase tracking-wide ${cls}`} {...props}>
      {t(`badges.${(priority || "").toLowerCase()}`, { defaultValue: priority })}
    </Badge>
  );
}

export const fmtDate = (s) =>
  s ? new Date(s).toLocaleDateString(curLocale(), { day: "2-digit", month: "short", year: "numeric" }) : "—";

export const fmtDateTime = (s) =>
  s
    ? new Date(s).toLocaleString(curLocale(), { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })
    : "—";

export const fmtMoney = (n, currency = "USD") =>
  `${currency} ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const FILE_CATEGORIES = ["Shop Drawing", "Method Statement", "Material Approval", "Photo", "IPC", "Contract", "Other"];
export const RFI_PRIORITIES = ["Low", "Medium", "High", "Urgent"];
export const ROLE_SUGGESTIONS = ["Client", "Client Consultant", "Client Designer", "Designer", "Sub-Contractor", "Lead Designer of Structure", "Acrylic Supplier", "Supplier", "Main Contractor", "Consultant"];
